import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import ProcedureService from '../../services/procedureService';
import './MultiProcedureSelector.css';

const MultiProcedureSelector = ({ 
  selectedProcedures = [], 
  onProceduresChange, 
  placeholder = "Buscar y agregar procedimientos...",
  disabled = false,
  showCost = true  // New prop to control cost display
}) => {
  const auth = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableProcedures, setAvailableProcedures] = useState([]);
  const [filteredProcedures, setFilteredProcedures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const loadProcedures = useCallback(async () => {
    if (!auth.user?.access_token) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await ProcedureService.getAllProcedures(auth.user.access_token);
      setAvailableProcedures(response.data || []);
    } catch (err) {
      console.error('Error loading procedures:', err);
      setError('Error al cargar procedimientos');
    } finally {
      setLoading(false);
    }
  }, [auth.user?.access_token]);

  // Load initial procedures
  useEffect(() => {
    loadProcedures();
  }, [loadProcedures]);

  // Filter procedures based on search term and exclude already selected ones
  useEffect(() => {
    const selectedIds = selectedProcedures.map(p => p.procedureId);
    let filtered = availableProcedures.filter(proc => 
      !selectedIds.includes(proc.id)
    );

    if (searchTerm.trim()) {
      filtered = filtered.filter(procedure =>
        procedure.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProcedures(filtered.slice(0, 10)); // Limit to 10 results
  }, [searchTerm, availableProcedures, selectedProcedures]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setSearchTerm('');
    }
  };

  const handleAddProcedure = (procedure) => {
    const newProcedure = {
      procedureId: procedure.id,
      procedureName: procedure.name,
      quantity: 1,
      cost: procedure.cost
    };
    
    onProceduresChange([...selectedProcedures, newProcedure]);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleRemoveProcedure = (procedureId) => {
    const updated = selectedProcedures.filter(p => p.procedureId !== procedureId);
    onProceduresChange(updated);
  };

  const handleUpdateQuantity = (procedureId, newQuantity) => {
    if (newQuantity < 1 || newQuantity > 100) return;
    
    const updated = selectedProcedures.map(p => 
      p.procedureId === procedureId 
        ? { ...p, quantity: parseInt(newQuantity) }
        : p
    );
    onProceduresChange(updated);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (e.key === 'Enter' && filteredProcedures.length > 0) {
      handleAddProcedure(filteredProcedures[0]);
    }
  };

  const getTotalQuantity = () => {
    return selectedProcedures.reduce((sum, proc) => sum + proc.quantity, 0);
  };

  return (
    <div className={`multi-procedure-selector ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      {/* Selected Procedures List */}
      {selectedProcedures.length > 0 && (
        <div className="selected-procedures-list">
          <div className="selected-procedures-header">
            <span className="procedures-count">
              {selectedProcedures.length} procedimiento{selectedProcedures.length !== 1 ? 's' : ''} seleccionado{selectedProcedures.length !== 1 ? 's' : ''}
              {getTotalQuantity() > selectedProcedures.length && (
                <span className="total-quantity"> (Total: {getTotalQuantity()})</span>
              )}
            </span>
          </div>
          
          <div className="selected-procedures-grid">
            {selectedProcedures.map((procedure) => (
              <div key={procedure.procedureId} className="selected-procedure-item">
                <div className="procedure-info">
                  <div className="procedure-name">{procedure.procedureName}</div>
                  {showCost && procedure.cost && (
                    <div className="procedure-cost">
                      ${parseFloat(procedure.cost).toLocaleString()} c/u
                    </div>
                  )}
                </div>
                
                <div className="quantity-controls">
                  <button
                    type="button"
                    className="quantity-btn"
                    onClick={() => handleUpdateQuantity(procedure.procedureId, procedure.quantity - 1)}
                    disabled={procedure.quantity <= 1}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    className="quantity-input"
                    value={procedure.quantity}
                    onChange={(e) => handleUpdateQuantity(procedure.procedureId, e.target.value)}
                    min="1"
                    max="100"
                  />
                  <button
                    type="button"
                    className="quantity-btn"
                    onClick={() => handleUpdateQuantity(procedure.procedureId, procedure.quantity + 1)}
                    disabled={procedure.quantity >= 100}
                  >
                    +
                  </button>
                </div>

                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => handleRemoveProcedure(procedure.procedureId)}
                  title="Quitar procedimiento"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Procedure Dropdown */}
      <div className="add-procedure-section">
        <div 
          className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
          onClick={handleToggleDropdown}
        >
          <div className="trigger-content">
            <i className="fas fa-plus"></i>
            <span>{placeholder}</span>
          </div>
          <i className={`fas fa-chevron-down dropdown-arrow ${isOpen ? 'open' : ''}`}></i>
        </div>

        {isOpen && (
          <div className="dropdown-menu">
            <div className="search-container">
              <i className="fas fa-search search-icon"></i>
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder="Buscar procedimiento..."
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
              />
            </div>

            <div className="procedures-list">
              {loading ? (
                <div className="loading-item">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Cargando procedimientos...</span>
                </div>
              ) : error ? (
                <div className="error-item">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>{error}</span>
                </div>
              ) : filteredProcedures.length === 0 ? (
                <div className="empty-item">
                  <i className="fas fa-search"></i>
                  <span>
                    {searchTerm ? 'No se encontraron procedimientos' : 'Todos los procedimientos están seleccionados'}
                  </span>
                </div>
              ) : (
                filteredProcedures.map((procedure) => (
                  <div
                    key={procedure.id}
                    className="procedure-item"
                    onClick={() => handleAddProcedure(procedure)}
                  >
                    <div className="procedure-info">
                      <div className="procedure-name">{procedure.name}</div>
                      {showCost && procedure.cost && (
                        <div className="procedure-cost">
                          ${parseFloat(procedure.cost).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <i className="fas fa-plus add-icon"></i>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiProcedureSelector;
